import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";

import HintModal from "../common/hint-modal";

import FaxTemplateEditor from "./FaxTemplateEditor";
import useFaxSettings from "./hooks/useFaxSettings";

import "./fax-settings.css";

const UPLOAD_STEP = "upload";
const EDIT_TEMPLATE_STEP = "edit-template";
const PROVIDER_STEP = "efax-provider";
const MAX_TEMPLATE_SIZE = 10 * 1024 * 1024;
const PDF_TEMPLATE = "pdf";
const WORD_TEMPLATE = "word";

const formatFileSize = (size) => {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeDomain = (domain) => domain.trim().replace(/^@+/, "").toLowerCase();

const validateCustomDomain = (domain) => {
  const normalized = normalizeDomain(domain);
  if (!normalized) return "Enter the custom electronic fax provider domain.";
  if (normalized.includes("@") || /\s/.test(normalized) || !normalized.includes(".")) {
    return "Enter a bare domain such as myfaxgateway.com (without an email address).";
  }
  return "";
};

const normalizeEmailList = (emails) =>
  [
    ...new Set(
      (Array.isArray(emails) ? emails : [])
        .map((email) => String(email).trim().toLowerCase())
        .filter(Boolean)
    ),
  ].sort();

const FaxSettings = () => {
  const pdfFileInputRef = useRef(null);
  const wordFileInputRef = useRef(null);
  const templateEditorRef = useRef(null);
  const changingStepRef = useRef(false);
  const initializedProviderRef = useRef(false);
  const initializedEmailsRef = useRef(false);
  const [activeStep, setActiveStep] = useState(UPLOAD_STEP);
  const [selectedFile, setSelectedFile] = useState(null);
  const [provider, setProvider] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [templateDirty, setTemplateDirty] = useState(false);
  const [draggingTemplate, setDraggingTemplate] = useState("");
  const [actionError, setActionError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [showHints, setShowHints] = useState(false);

  const {
    settings,
    variables,
    firmEmails,
    loadingSettings,
    loadingVariables,
    loadingFirmEmails,
    saving,
    settingsError,
    variablesError,
    firmEmailsError,
    loadSettings,
    loadVariables,
    loadFirmEmails,
    saveSettings,
  } = useFaxSettings();

  useEffect(() => {
    if (!settings || initializedProviderRef.current) return;
    initializedProviderRef.current = true;
    setProvider(settings.efax_provider || "");
    setCustomDomain(settings.custom_sending_domain || "");
  }, [settings]);

  useEffect(() => {
    if (!settings || loadingFirmEmails || firmEmailsError || initializedEmailsRef.current) {
      return;
    }

    const availableEmails = new Set(firmEmails.map(({ email }) => email.toLowerCase()));
    const savedEmails = Array.isArray(settings.selected_emails) ? settings.selected_emails : [];
    setSelectedEmails(
      savedEmails.filter((email) => availableEmails.has(String(email).toLowerCase()))
    );
    initializedEmailsRef.current = true;
  }, [firmEmails, firmEmailsError, loadingFirmEmails, settings]);

  const clearMessages = () => {
    setActionError("");
    setSuccessMessage("");
  };

  const selectedTemplateType = selectedFile
    ? /\.pdf$/i.test(selectedFile.name)
      ? PDF_TEMPLATE
      : WORD_TEMPLATE
    : "";

  const acceptFile = (file, templateType) => {
    if (!file || saving) return;
    clearMessages();

    const isValidType =
      templateType === PDF_TEMPLATE ? /\.pdf$/i.test(file.name) : /\.docx?$/i.test(file.name);

    if (!isValidType) {
      setSelectedFile(null);
      setActionError(
        templateType === PDF_TEMPLATE
          ? "Choose a PDF file for the PDF cover sheet template."
          : "Choose a Microsoft Word (.doc or .docx) file for the Word cover sheet template."
      );
      return;
    }
    if (file.size === 0) {
      setSelectedFile(null);
      setActionError("The selected document is empty. Choose a valid PDF or Word file.");
      return;
    }
    if (file.size > MAX_TEMPLATE_SIZE) {
      setSelectedFile(null);
      setActionError("The cover sheet template must be 10 MB or smaller.");
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || saving) return;
    clearMessages();
    const formData = new FormData();
    formData.append("cover_sheet_template", selectedFile);

    try {
      await saveSettings(formData);
      setSelectedFile(null);
      setSuccessMessage("Cover sheet uploaded. Add fax variables in the editor.");
      setActiveStep(EDIT_TEMPLATE_STEP);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleRemoveTemplate = async () => {
    if (saving) return;
    clearMessages();
    try {
      await saveSettings({ remove_cover_sheet_template: "true" });
      setSelectedFile(null);
      setShowRemoveConfirmation(false);
      setActiveStep(UPLOAD_STEP);
      setSuccessMessage(
        "The uploaded template was removed. The built-in cover sheet will be used."
      );
    } catch (error) {
      setShowRemoveConfirmation(false);
      setActionError(error.message);
    }
  };

  const selectedProviderOption = settings?.provider_options?.find(
    (option) => option.value === provider
  );
  const normalizedCustomDomain = normalizeDomain(customDomain);
  const displayedDomain =
    provider === "custom" ? normalizedCustomDomain : selectedProviderOption?.domain || "";
  const providerIsDirty =
    provider !== (settings?.efax_provider || "") ||
    (provider === "custom" &&
      normalizedCustomDomain !== normalizeDomain(settings?.custom_sending_domain || ""));
  const selectedEmailValues = normalizeEmailList(selectedEmails);
  const savedEmailValues = normalizeEmailList(settings?.selected_emails || []);
  const emailsAreDirty =
    selectedEmailValues.length !== savedEmailValues.length ||
    selectedEmailValues.some((email, index) => email !== savedEmailValues[index]);
  const providerSettingsAreDirty = providerIsDirty || emailsAreDirty;

  const toggleSelectedEmail = (email) => {
    clearMessages();
    setSelectedEmails((current) =>
      current.some((selectedEmail) => selectedEmail.toLowerCase() === email.toLowerCase())
        ? current.filter((selectedEmail) => selectedEmail.toLowerCase() !== email.toLowerCase())
        : [...current, email]
    );
  };

  const handleSaveProvider = async () => {
    if (saving || !provider) return;
    clearMessages();

    if (provider === "custom") {
      const validationError = validateCustomDomain(customDomain);
      if (validationError) {
        setActionError(validationError);
        return;
      }
    }

    const payload = { efax_provider: provider, selected_emails: selectedEmails };
    if (provider === "custom") payload.custom_sending_domain = normalizedCustomDomain;

    try {
      const updatedSettings = await saveSettings(payload);
      setProvider(updatedSettings.efax_provider || "");
      setCustomDomain(updatedSettings.custom_sending_domain || "");
      setSelectedEmails(
        Array.isArray(updatedSettings.selected_emails)
          ? updatedSettings.selected_emails
          : selectedEmails
      );
      setSuccessMessage("E-fax provider and sending email settings saved.");
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleEditorError = useCallback((message) => {
    setSuccessMessage("");
    setActionError(message);
  }, []);

  const handleEditorSuccess = useCallback((message) => {
    setActionError("");
    setSuccessMessage(message);
  }, []);

  const changeStep = async (step) => {
    if (activeStep === EDIT_TEMPLATE_STEP && templateDirty && step !== EDIT_TEMPLATE_STEP) {
      if (changingStepRef.current || saving) return;

      changingStepRef.current = true;
      clearMessages();
      const saved = await templateEditorRef.current?.saveEditedTemplate({ silent: true });
      changingStepRef.current = false;
      if (!saved) return;
    }

    setActiveStep(step);
    clearMessages();
  };

  const hintPoints = [
    "Upload either one PDF cover sheet template or one Microsoft Word (.doc or .docx) template. Selecting a file disables the other upload area.",
    "Open the Edit Template tab, place the cursor in the document, and click a fax variable to insert it.",
    "Choose the firm's e-fax provider. Fax numbers will be combined with that provider's sending domain.",
  ];

  if (loadingSettings) {
    return (
      <div className="fax-settings-state" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        Loading fax settings…
      </div>
    );
  }

  if (settingsError || !settings) {
    return (
      <div className="fax-settings-state fax-settings-state-error" role="alert">
        <strong>Fax settings could not be loaded.</strong>
        <span>{settingsError?.message}</span>
        <button
          type="button"
          className="btn btn-primary height-25"
          onClick={() => loadSettings().catch(() => {})}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="fax-settings-shell">
        <div className="fax-settings-header">
          <div>
            <h2>Fax Settings</h2>
            <p>
              Configure the cover sheet and electronic fax provider used for outgoing case faxes.
            </p>
          </div>
          <button
            type="button"
            className="fax-settings-help"
            aria-label="Fax settings help"
            onClick={() => setShowHints(true)}
          >
            <i className="ic ic-19 ic-help-2" />
          </button>
        </div>

        <nav className="fax-settings-wizard" aria-label="Fax settings steps">
          <button
            type="button"
            className={`fax-settings-wizard-step fax-settings-wizard-first ${
              activeStep === UPLOAD_STEP ? "active" : ""
            }`}
            aria-current={activeStep === UPLOAD_STEP ? "step" : undefined}
            onClick={() => changeStep(UPLOAD_STEP)}
          >
            <span>1:</span> Upload Cover Sheet Template
          </button>
          <button
            type="button"
            disabled={!settings.cover_sheet_template_url}
            className={`fax-settings-wizard-step ${
              activeStep === EDIT_TEMPLATE_STEP ? "active" : ""
            }`}
            aria-current={activeStep === EDIT_TEMPLATE_STEP ? "step" : undefined}
            onClick={() => changeStep(EDIT_TEMPLATE_STEP)}
          >
            <span>2:</span> Edit Template
          </button>
          <button
            type="button"
            className={`fax-settings-wizard-step fax-settings-wizard-last ${
              activeStep === PROVIDER_STEP ? "active" : ""
            }`}
            aria-current={activeStep === PROVIDER_STEP ? "step" : undefined}
            onClick={() => changeStep(PROVIDER_STEP)}
          >
            <span>3:</span> E-Fax Provider
          </button>
        </nav>

        {(actionError || successMessage) && (
          <div
            className={`fax-settings-message ${actionError ? "is-error" : "is-success"}`}
            role={actionError ? "alert" : "status"}
          >
            <span className="fax-settings-message-icon">{actionError ? "!" : "✓"}</span>
            <span>{actionError || successMessage}</span>
          </div>
        )}

        {activeStep === UPLOAD_STEP ? (
          <div className="fax-settings-cover-layout fax-settings-upload-layout">
            <section className="fax-settings-panel" aria-labelledby="fax-cover-title">
              <div className="fax-settings-panel-heading">
                <div>
                  <h3 id="fax-cover-title">Fax Cover Sheet</h3>
                  <p>The template is automatically filled and placed before every fax.</p>
                </div>
                {/* <span className="fax-settings-required">DOCX · MAX 10 MB</span> */}
              </div>

              <div className="fax-settings-panel-body">
                <div className="fax-settings-dropzones">
                  {[
                    {
                      type: PDF_TEMPLATE,
                      inputRef: pdfFileInputRef,
                      label: "Drop your PDF cover sheet template here.",
                      accept: ".pdf,application/pdf",
                      formatLabel: "PDF files",
                      replacementLabel: "Click or drop another PDF file to replace this selection.",
                    },
                    {
                      type: WORD_TEMPLATE,
                      inputRef: wordFileInputRef,
                      label: "Drop your DOC or DOCX template here",
                      accept:
                        ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      formatLabel: "Microsoft Word (.doc or .docx) files",
                      replacementLabel:
                        "Click or drop another Word file to replace this selection.",
                    },
                  ].map((uploadOption) => {
                    const hasFile = selectedTemplateType === uploadOption.type;
                    const disabled = saving || Boolean(selectedTemplateType && !hasFile);

                    return (
                      <div
                        key={uploadOption.type}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-disabled={disabled}
                        className={`fax-settings-dropzone ${
                          draggingTemplate === uploadOption.type ? "is-dragging" : ""
                        } ${hasFile ? "has-file" : ""} ${disabled ? "is-disabled" : ""}`}
                        onDragOver={(event) => {
                          event.preventDefault();
                          if (!disabled) setDraggingTemplate(uploadOption.type);
                        }}
                        onDragLeave={() => setDraggingTemplate("")}
                        onDrop={(event) => {
                          event.preventDefault();
                          setDraggingTemplate("");
                          if (!disabled) {
                            acceptFile(event.dataTransfer.files?.[0], uploadOption.type);
                          }
                        }}
                        onClick={() => !disabled && uploadOption.inputRef.current?.click()}
                        onKeyDown={(event) => {
                          if (!disabled && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault();
                            uploadOption.inputRef.current?.click();
                          }
                        }}
                      >
                        <div className="fax-settings-upload-icon">{hasFile ? "✓" : "↑"}</div>
                        <strong>{hasFile ? selectedFile.name : uploadOption.label}</strong>
                        <span>
                          {hasFile
                            ? formatFileSize(selectedFile.size)
                            : disabled && selectedTemplateType
                              ? "Another template type is already selected"
                              : "or click to browse your computer"}
                        </span>
                        <small>
                          {hasFile ? uploadOption.replacementLabel : uploadOption.formatLabel}
                        </small>
                        <input
                          ref={uploadOption.inputRef}
                          type="file"
                          accept={uploadOption.accept}
                          className="d-none"
                          disabled={disabled}
                          onChange={(event) => {
                            acceptFile(event.target.files?.[0], uploadOption.type);
                            event.target.value = "";
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="fax-settings-current-template">
                  <div className="fax-settings-file-icon">W</div>
                  <div className="fax-settings-file-details">
                    <span>Current cover sheet</span>
                    <strong>
                      {settings.cover_sheet_template_name || "Built-in SimpleFirm cover sheet"}
                    </strong>
                  </div>
                  <div className="fax-settings-file-actions">
                    {settings.cover_sheet_template_url && (
                      <button
                        type="button"
                        className="btn btn-primary height-25 d-flex align-items-center justify-content-center"
                        disabled={saving}
                        onClick={() => changeStep(EDIT_TEMPLATE_STEP)}
                      >
                        Open
                      </button>
                    )}
                    {settings.cover_sheet_template_name && (
                      <button
                        type="button"
                        className="btn btn-outline-danger height-25 d-flex align-items-center justify-content-center"
                        disabled={saving}
                        onClick={() => setShowRemoveConfirmation(true)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div className="fax-settings-actions">
                  <button
                    type="button"
                    className={`btn height-25 ${selectedFile ? "btn-success" : "btn-secondary"}`}
                    disabled={!selectedFile || saving}
                    onClick={handleUpload}
                  >
                    {saving ? "Saving…" : "Save Cover Sheet"}
                  </button>
                </div>
              </div>
            </section>
          </div>
        ) : activeStep === EDIT_TEMPLATE_STEP ? (
          <FaxTemplateEditor
            ref={templateEditorRef}
            templateUrl={settings.cover_sheet_template_url}
            templateName={settings.cover_sheet_template_name}
            variables={variables}
            loadingVariables={loadingVariables}
            variablesError={variablesError}
            loadVariables={loadVariables}
            saving={saving}
            onSave={saveSettings}
            onError={handleEditorError}
            onSuccess={handleEditorSuccess}
            onDirtyChange={setTemplateDirty}
          />
        ) : (
          <section className="fax-settings-panel fax-settings-provider-panel">
            <div className="fax-settings-panel-heading p-r-5">
              <div>
                <h3>E-Fax Provider</h3>
                <p>Select the electronic fax provider your firm uses to send outgoing faxes.</p>
              </div>
              {settings.efax_provider_label && (
                <span className="fax-settings-provider-badge">
                  Current: {settings.efax_provider_label}
                </span>
              )}
            </div>

            <div className="fax-settings-provider-body">
              <fieldset className="fax-settings-provider-options" disabled={saving}>
                <legend>Provider</legend>
                {(settings.provider_options || []).map((option) => (
                  <label
                    key={option.value}
                    className={`fax-settings-provider-option ${
                      provider === option.value ? "is-selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      className="fax-settings-selection-input"
                      name="efax-provider"
                      value={option.value}
                      checked={provider === option.value}
                      onChange={(event) => {
                        setProvider(event.target.value);
                        clearMessages();
                      }}
                    />
                    <span className="fax-settings-provider-copy">
                      <strong>{option.label}</strong>
                      <small>
                        {option.domain
                          ? `Fax gateway: @${option.domain}`
                          : "Use your firm's custom electronic fax provider"}
                      </small>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="fax-settings-provider-summary">
                <div className="fax-settings-summary-heading">
                  <span>Sending address</span>
                  <strong>{selectedProviderOption?.label || "Select a provider"}</strong>
                </div>

                {provider === "custom" && (
                  <div className="fax-settings-domain-field">
                    <label htmlFor="fax-custom-domain">Electronic fax provider domain</label>
                    <div className="fax-settings-domain-input">
                      <span>@</span>
                      <input
                        id="fax-custom-domain"
                        type="text"
                        className="form-control height-25"
                        value={customDomain}
                        placeholder="myfaxgateway.com"
                        autoComplete="off"
                        disabled={saving}
                        onChange={(event) => {
                          setCustomDomain(event.target.value);
                          clearMessages();
                        }}
                        onBlur={() => setCustomDomain(normalizeDomain(customDomain))}
                      />
                    </div>
                    <small>Enter only the domain—not a full email address.</small>
                  </div>
                )}

                <div className="fax-settings-address-preview">
                  <span>Example</span>
                  <strong>5554443333@{displayedDomain || "provider-domain.com"}</strong>
                  <p>
                    When a fax is sent, SimpleFirm removes formatting from the fax number and adds
                    this domain.
                  </p>
                </div>
              </div>
            </div>

            <div className="fax-settings-email-selection">
              <div className="fax-settings-email-heading">
                <div>
                  <h4>Sending Email Accounts</h4>
                  <p>Select the connected firm email accounts that can send outgoing faxes.</p>
                </div>
                <span>{selectedEmails.length} selected</span>
              </div>

              {loadingFirmEmails ? (
                <div className="fax-settings-email-state" role="status">
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Loading connected firm emails…
                </div>
              ) : firmEmailsError ? (
                <div className="fax-settings-email-state is-error" role="alert">
                  <span>{firmEmailsError.message}</span>
                  <button type="button" onClick={() => loadFirmEmails().catch(() => {})}>
                    Retry
                  </button>
                </div>
              ) : firmEmails.length ? (
                <div className="fax-settings-email-options">
                  {firmEmails.map((firmEmail) => {
                    const checked = selectedEmails.some(
                      (email) => email.toLowerCase() === firmEmail.email.toLowerCase()
                    );
                    return (
                      <label
                        key={firmEmail.id}
                        className={`fax-settings-email-option mb-0 ${checked ? "is-selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          className="fax-settings-selection-input"
                          checked={checked}
                          disabled={saving}
                          onChange={() => toggleSelectedEmail(firmEmail.email)}
                        />
                        <span className="fax-settings-email-copy">
                          <strong>{firmEmail.email}</strong>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="fax-settings-email-state">
                  No connected firm email accounts are available. Connect an account in Client
                  Communication settings first.
                </div>
              )}
            </div>

            <div className="fax-settings-actions fax-settings-provider-actions justify-content-end">
              <button
                type="button"
                className={`btn height-25 ${providerSettingsAreDirty ? "btn-success" : "btn-secondary"}`}
                disabled={!providerSettingsAreDirty || saving || !provider || loadingFirmEmails}
                onClick={handleSaveProvider}
              >
                {saving ? "Saving…" : "Save Fax Delivery"}
              </button>
            </div>
          </section>
        )}
      </div>

      <Modal
        show={showRemoveConfirmation}
        onHide={() => !saving && setShowRemoveConfirmation(false)}
        dialogClassName="fax-settings-modal modal-dialog-centered"
        contentClassName="custom-modal-new-provider"
        centered
      >
        <Modal.Header className="tfbp-modal-header">
          <Modal.Title className="tfbp-modal-title">Use the Built-In Cover Sheet?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            This removes the firm’s uploaded cover sheet template. Future faxes will use the
            built-in SimpleFirm cover sheet.
          </p>
        </Modal.Body>
        <Modal.Footer className="p-0 mt-0 padding-outside-btn-new-provider">
          <Button
            variant="secondary"
            className="button-padding-footer-new-provider height-25 d-flex align-items-center justify-content-center"
            disabled={saving}
            onClick={() => setShowRemoveConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="button-padding-footer-new-provider height-25 d-flex align-items-center justify-content-center"
            disabled={saving}
            onClick={handleRemoveTemplate}
          >
            {saving ? "Removing…" : "Remove Template"}
          </Button>
        </Modal.Footer>
      </Modal>

      {showHints && (
        <HintModal show={showHints} handleClose={() => setShowHints(false)} points={hintPoints} />
      )}
    </>
  );
};

export default FaxSettings;
